import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { ArrowRight, Sparkles } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import usePageSEO from "@/hooks/usePageSEO";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "young-family", label: "Young Family" },
  { id: "mid-career", label: "Mid-Career" },
  { id: "pre-retirement", label: "Pre-Retirement" },
  { id: "doctor", label: "Doctor" },
  { id: "business-owner", label: "Business Owner" },
  { id: "w2-executive", label: "W-2 Executive" },
];

export default function Scenarios() {
  const [scenarios, setScenarios] = useState([]);
  const [filter, setFilter] = useState("all");

  usePageSEO({
    title: "Case Studies — How layered strategy adapts to real lives",
    description: "Educational, hypothetical case studies showing layered insurance + retirement strategies for doctors, business owners, families, and pre-retirees.",
  });

  useEffect(() => {
    let cancelled = false;
    apiGet("/scenarios").then((data) => {
      if (!cancelled) setScenarios(data || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const filtered = scenarios.filter((s) => filter === "all" || s.filter_tags.includes(filter));

  return (
    <div data-testid="scenarios-index" className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">
        <section className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
          <span className="lw-eyebrow">Case studies</span>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-lw-navy leading-[1.05] max-w-3xl">
            See how the right strategy adapts to real lives.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-lw-muted leading-relaxed max-w-2xl">
            Hypothetical case studies — designed to be educational, not testimonial. Each shows a layered protect / accumulate / transfer strategy applied to a realistic situation.
          </p>

          <div data-testid="scenario-filters" className="flex flex-wrap gap-2 mt-8">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`filter-${f.id}`}
                className={`lw-pill ${filter === f.id ? "bg-lw-navy text-lw-ivory border-lw-navy" : "hover:bg-lw-gold/15"}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            {filtered.map((s) => (
              <Link
                key={s.slug}
                to={`/insights/scenarios/${s.slug}`}
                data-testid={`scenario-card-${s.slug}`}
                className="lw-card p-7 group flex flex-col h-full"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-lw-gold/15 border border-lw-gold/30 flex items-center justify-center text-lw-gold">
                    <Sparkles size={22} />
                  </div>
                  <div>
                    <span className="lw-eyebrow text-[10px]">Age {s.age}</span>
                    <h3 className="mt-1 font-serif-display text-xl text-lw-navy leading-tight">{s.headline}</h3>
                  </div>
                </div>
                <p className="text-sm text-lw-muted leading-relaxed">{s.summary}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold text-lw-navy group-hover:text-[#8a6a2b]">Read full scenario <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
